/**
 * The browser tab title, per route.
 *
 * Nina lives in her coding agent's tab and comes back to this one to find out
 * whether the thing she pushed is alive. A window of eleven tabs all reading
 * "SV Cloud" makes her open three of them to find the one she wants, so the
 * tab has to say which project it is on before it says which product it is.
 * That is the whole reason the project name comes first and the product name
 * comes last: a tab strip truncates from the right.
 *
 *   /p/herb-and-ash            Herb and Ash · SV Cloud
 *   /p/herb-and-ash/deploys    Deploys · Herb and Ash · SV Cloud
 *   /waitlist                  Waitlist · SV Cloud
 *
 * The status aware favicon from screen spec 1.3d is deferred, so the tab
 * carries the static mark and the title carries the location. When the canvas
 * favicon lands it mounts beside this, reading the watched deploy, and nothing
 * here has to change.
 *
 * The parts are joined rather than written as one string on purpose. These are
 * independent labels in a hierarchy, exactly like the breadcrumb, not a
 * sentence split around an interpolation, so joining them does not put a
 * translator in the position of reordering words they cannot see.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router';

import { useProjects } from '../../lib/auth/use-app-data';
import { t } from '../../lib/i18n/t';
import type { Project } from '../../lib/types';

const PRODUCT = 'SV Cloud';
const JOIN = ' · ';

/** The section labels are the shipped nav labels, verbatim. A tab that names a
 *  screen differently from the rail is a tab about a different product. */
function projectSection(segments: string[]): string | null {
  const section = segments[2];
  const rest = segments[3];

  if (section === undefined) return null;

  switch (section) {
    case 'deploys':
      // One deploy has no stable short name here (the H1 is its commit
      // message, which is a sentence and would swamp the tab), so the tab says
      // what kind of page it is and lets the project name do the identifying.
      return rest ? t('Deploy') : t('Deploys');
    case 'database':
      if (rest === 'query') return t('Run a query');
      return rest ? decodeURIComponent(rest) : t('Database');
    case 'files':
      return t('Files');
    case 'keys':
      return t('Keys and settings');
    case 'add-ons':
      return t('Add-ons');
    case 'activity':
      return t('Activity');
    case 'logs':
      return t('Live logs');
    case 'traffic':
      return t('Traffic');
    case 'usage':
      return t('Usage');
    case 'settings':
      return t('Settings');
    case 'bundle':
      return t('Bundle version');
    default:
      return t('Not found');
  }
}

function topLevel(segments: string[]): string | null {
  switch (segments[0]) {
    case undefined:
      return null;
    case 'sign-in':
      return t('Sign in');
    case 'sign-up':
      return t('Create your account');
    case 'waitlist':
      return t('Waitlist');
    case 'privacy':
      return t('Privacy');
    case 'terms':
      return t('Terms of service');
    case 'invite':
      return t('Set up your account');
    case 'project-invite':
      return t('Invitation');
    case 'admin':
      if (segments[1] === 'waitlist') return t('Wait list');
      if (segments[1] === 'god-view') return t('God View');
      if (segments[1] === 'billing') return t('Plans & credits');
      if (segments[1] === 'platform') return t('Platform');
      if (segments[1] === 'feedbacks') return t('Feedbacks');
      return t('Not found');
    case 'new':
      if (segments[1] === 'code') return t('Where your code lives');
      if (segments[1] === 'connect') return t('Connect your code');
      if (segments[1] === 'building') return t('Your first deploy');
      return t('New project');
    case 'setup':
      return t('Set up your computer');
    case 'projects':
      return t('Projects');
    case 'account':
      return t('Account');
    case 'usage':
      return t('Usage');
    // The upload link. Deliberately generic: the tab is the one part of this
    // page a stranger might leave open on a shared screen, and the label the
    // owner wrote for the delivery is not ours to put there.
    case 'u':
      return t('Upload files');
    case 'claim':
      return t('Claim your project');
    case 'deadline':
      return t('Deadline notice');
    case 'kit':
      return t('Kit');
    default:
      return t('Not found');
  }
}

function nameOf(projects: Project[], slug: string): string {
  // An address with a typo in it still gets a useful tab: the slug is what the
  // person typed, so echoing it beats a tab that says nothing.
  return projects.find((project) => project.slug === slug)?.name ?? slug;
}

export function titleFor(pathname: string, projects: Project[]): string {
  const segments = pathname.split('/').filter(Boolean);

  if (segments[0] === 'p' && segments[1]) {
    const name = nameOf(projects, decodeURIComponent(segments[1]));
    const section = projectSection(segments);
    return [section, name, PRODUCT].filter(Boolean).join(JOIN);
  }

  // The welcome screen is a hat on a project, so it is titled as one.
  if (segments[0] === 'welcome' && segments[1]) {
    return [nameOf(projects, decodeURIComponent(segments[1])), PRODUCT].join(JOIN);
  }

  const label = topLevel(segments);
  return label ? [label, PRODUCT].join(JOIN) : PRODUCT;
}

export function DocumentTitle() {
  const { pathname } = useLocation();
  const projects = useProjects();
  const title = titleFor(pathname, projects);

  useEffect(() => {
    document.title = title;
  }, [title]);

  return null;
}
